import { useQueryParams } from '@calendly/hooks'
import { useRouteMatch } from 'react-router-dom'

import { historyPush } from 'actions/core/navigation'

const TAB_PATH_BY_NAME = {
  event_types: '/event_types',
  scheduling: '/app/scheduling',
  scheduling_meeting_types: '/app/scheduling/meeting_types',
  scheduling_single_use_links: '/app/scheduling/single_use_links',
  scheduling_polls: '/app/scheduling/polls',
  scheduled_events: '/app/scheduled_events',
  workflows: '/app/workflows',
  landing_pages: '/app/landing_pages',
  routing: '/app/routing/forms',
  contacts: '/app/contacts',
  meetings: '/app/meetings',
  recaps: '/app/recaps'
}

const TAB_NAME_BY_PATH = {
  '/event_types': 'event_types',
  '/app/scheduling/meeting_types': 'scheduling',
  '/app/scheduling/single_use_links': 'scheduling',
  '/app/scheduling/polls': 'scheduling',
  '/app/scheduled_events': 'scheduled_events',
  '/app/workflows': 'workflows',
  '/app/landing_pages': 'landing_pages',
  '/app/routing/forms': 'routing',
  '/app/contacts': 'contacts',
  '/app/meetings': 'meetings',
  '/app/recaps': 'recaps'
}

export const homePaths = [
  `(${Object.values(TAB_PATH_BY_NAME).join('|')})`,
  '(user|team|user_group|all_users|all_users_and_teams)',
  ':id?'
].join('/')

const getTabPath = (params) => params[0]

const getScopeType = (params) => params[1] || 'user'

const getScopeId = (params) => (params[1] ? params.id : 'me')

const getScopePath = (params) => {
  const type = getScopeType(params)
  const id = getScopeId(params)

  return id ? `${type}/${id}` : type
}

const getScopeTypeV1 = (params) => {
  const type = getScopeType(params)
  const id = getScopeId(params)

  switch (type) {
    case 'user': {
      if (id === 'me') {
        return 'my_calendly'
      } if (id) {
        return 'user_single'
      }
      return 'all_users'
    }
    case 'team': {
      if (id) {
        return 'page_single'
      }
      return 'page'
    }
    case 'all_users_and_teams':
      return 'all_users_and_teams'
    case 'all_users':
      return 'all_users'
    case 'user_group':
      return 'user_group'
    default:
      return null
  }
}

const getScopeTypeV2 = (params) => {
  const type = getScopeType(params)
  const id = getScopeId(params)

  switch (type) {
    case 'all_users_and_teams':
      return 'all_users_and_teams'
    case 'all_users':
      return 'users'
    case 'user_group': {
      return 'user_group'
    }
    case 'user': {
      if (id) {
        return 'user'
      }
      return 'users'
    }
    case 'team': {
      if (id) {
        return 'team'
      }
      return 'teams'
    }
    default:
      return null
  }
}


const tabNameToPath = (name) => (
  TAB_PATH_BY_NAME[name]
)

const scopeV1ToPath = ({ type, id }) => {
  switch (type) {
    case 'my_calendly':
      return 'user/me'
    case 'all_users_and_teams':
      return 'all_users_and_teams'
    case 'all_users':
      return 'all_users'
    case 'page':
      return 'team'
    case 'user_single':
      return `user/${id}`
    case 'page_single':
      return `team/${id}`
    case 'user_group':
      return `user_group/${id}`
    default:
      return null
  }
}

/*
 * TODO: Get rid of scope versions.
 */

export const useHomeTabs = () => {
  const { params = {} } = useRouteMatch(homePaths) || {}
  const queryParams = useQueryParams()

  const getTabName = () => (
    TAB_NAME_BY_PATH[getTabPath(params)]
  )

  const getTabScopeV1 = () => {
    const type = getScopeTypeV1(params)
    const id = getScopeId(params)
    return { type, id }
  }

  const getTabScopeV2 = () => {
    const type = getScopeTypeV2(params)
    const id = getScopeId(params)
    return { type, id }
  }

  const getTabPathByName = (name) => {
    const tabPath = tabNameToPath(name)
    const scopePath = getScopePath(params)

    return `${tabPath}/${scopePath}`
  }

  const setTabScopeV1 = (scope) => (dispatch) => {
    const tabPath = getTabPath(params)
    const scopePath = scopeV1ToPath(scope)
    let path = `${tabPath}/${scopePath}`
    if (getTabName() === 'meetings') {
      // Strip meeting selection and right rail params when changing scope - the selected meeting
      // may not exist in the new scope, and keeping them causes the rail to stay open and can
      // trigger "Maximum update depth exceeded" before the page's scope-change handler runs.
      const { selected, share_id: shareId, pane, paneState, ...restParams } = queryParams
      path = `${path}?${new URLSearchParams(restParams).toString()}`
    }
    dispatch(historyPush(path))
  }

  return {
    getTabName,
    getTabPathByName,
    getTabScopeV1,
    setTabScopeV1,
    getTabScopeV2
  }
}
